import { toggle } from "nodash"
import { EventAction, EventCategory } from "@nova/schemas"
import classNames from "classnames"
import { subjectTypeOptions } from "features/events/eventLabels"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { actionsFor, CategorySelection } from "../activityFilters"
import { clearStagedCategory, selectStagedCategory, setStagedCategory } from "../slice"
import { FilterMenuShell, FilterOptionRow } from "./FilterMenuShell"
import { actionLabels, categoryLabels } from "./filterLabels"
import { Divider } from "components/Divider"

const allSubjectTypes = subjectTypeOptions.flatMap((option) => option.types)

const ActionChip = ({
    label,
    selected,
    onClick,
}: {
    label: string
    selected: boolean
    onClick: () => void
}) => (
    <button
        type="button"
        onClick={onClick}
        className={classNames(
            "px-2 py-0.5 rounded-full border text-xs whitespace-nowrap transition-colors",
            selected
                ? "border-blue-500 bg-blue-510 text-white"
                : "border-white-200 bg-black-500 text-white-500 hover:border-white-300"
        )}
    >
        {label}
    </button>
)

export const LayersFilterView = ({
    onBack,
    onCancel,
    onApply,
}: {
    onBack: () => void
    onCancel: () => void
    onApply: () => void
}) => {
    const dispatch = useAppDispatch()
    const selection = useAppSelector(selectStagedCategory(EventCategory.Layers))
    const actions = actionsFor(EventCategory.Layers)

    const update = (next: Partial<CategorySelection>) => {
        const updated = { ...selection, ...next }
        if (!updated.actions.length && !updated.subjectTypes.length) {
            dispatch(clearStagedCategory(EventCategory.Layers))
            return
        }
        dispatch(setStagedCategory({ category: EventCategory.Layers, selection: updated }))
    }

    const allSelected =
        selection.actions.length === actions.length &&
        selection.subjectTypes.length === allSubjectTypes.length

    return (
        <FilterMenuShell
            title={categoryLabels[EventCategory.Layers]}
            onBack={onBack}
            onCancel={onCancel}
            onApply={onApply}
            onSelectAll={
                allSelected ? undefined : () => update({ actions, subjectTypes: allSubjectTypes })
            }
            onClearAll={
                selection.actions.length || selection.subjectTypes.length
                    ? () => dispatch(clearStagedCategory(EventCategory.Layers))
                    : undefined
            }
            width="w-[398px]"
            bodyClassName="max-h-[320px] overflow-y-auto"
        >
            <div className="flex gap-4 pr-4 min-h-full">
                <div className="flex flex-col shrink-0 py-1">
                    {subjectTypeOptions.map((option) => {
                        const selected = option.types.every((type) =>
                            selection.subjectTypes.includes(type)
                        )
                        return (
                            <FilterOptionRow
                                key={option.label}
                                label={option.label}
                                selected={selected}
                                onClick={() =>
                                    update({
                                        subjectTypes: selected
                                            ? selection.subjectTypes.filter(
                                                  (type) => !option.types.includes(type)
                                              )
                                            : [...selection.subjectTypes, ...option.types],
                                    })
                                }
                            />
                        )
                    })}
                </div>

                <Divider vertical className="bg-gray-500 shrink-0 !w-px" />

                <div className="flex flex-wrap gap-1 content-start flex-1 min-w-0 py-1">
                    {actions.map((action: EventAction) => (
                        <ActionChip
                            key={action}
                            label={actionLabels[action]}
                            selected={selection.actions.includes(action)}
                            onClick={() => update({ actions: toggle(selection.actions, action) })}
                        />
                    ))}
                </div>
            </div>
        </FilterMenuShell>
    )
}
